import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9531Api, CalcParams, CalcResult, CalcStatus } from '../../../services/pw9531Api';

export function usePW9531Execute() {
  const currentDate = new Date();
  const [year, setYear] = useState<number>(currentDate.getFullYear());
  const [month, setMonth] = useState<number>(currentDate.getMonth() + 1);
  const [attendStartYear, setAttendStartYear] = useState<number>(currentDate.getFullYear());
  const [attendStartMonth, setAttendStartMonth] = useState<number>(currentDate.getMonth() + 1);
  const [attendEndYear, setAttendEndYear] = useState<number>(currentDate.getFullYear());
  const [attendEndMonth, setAttendEndMonth] = useState<number>(currentDate.getMonth() + 1);
  const [empFrom, setEmpFrom] = useState<string>('');
  const [empTo, setEmpTo] = useState<string>('ZZZZZZZZ');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [status, setStatus] = useState<CalcStatus | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const loadStatus = useCallback(async () => {
    try {
      const s = await pw9531Api.getStatus();
      setStatus(s);
    } catch {
      // Silently ignore status load failure
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const execute = useCallback(async () => {
    setLoading(true);
    setErrors([]);
    setResult(null);
    try {
      const params: CalcParams = {
        year, month,
        attendStartYear, attendStartMonth,
        attendEndYear, attendEndMonth,
        empFrom, empTo,
      };
      const res = await pw9531Api.execute(params);
      setResult(res);
      message.success(`薪資計算完成，共處理 ${res.processed} 位員工`);
      loadStatus();
    } catch (err: any) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const vErrs = err.response.data.errors as Array<{ field: string; message: string }>;
        setErrors(vErrs.map(e => e.message));
      } else {
        const msg = err.response?.data?.error || err.message || '執行失敗';
        setErrors([msg]);
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [year, month, attendStartYear, attendStartMonth, attendEndYear, attendEndMonth, empFrom, empTo, loadStatus]);

  const preview = useCallback(async () => {
    setLoading(true);
    setErrors([]);
    setResult(null);
    try {
      const params: CalcParams = {
        year, month,
        attendStartYear, attendStartMonth,
        attendEndYear, attendEndMonth,
        empFrom, empTo,
      };
      const res = await pw9531Api.preview(params);
      setResult(res);
      message.info(`預覽完成，共 ${res.processed} 位員工`);
    } catch (err: any) {
      if (err.response?.status === 422 && err.response?.data?.errors) {
        const vErrs = err.response.data.errors as Array<{ field: string; message: string }>;
        setErrors(vErrs.map(e => e.message));
      } else {
        const msg = err.response?.data?.error || err.message || '預覽失敗';
        setErrors([msg]);
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [year, month, attendStartYear, attendStartMonth, attendEndYear, attendEndMonth, empFrom, empTo]);

  return {
    year, setYear,
    month, setMonth,
    attendStartYear, setAttendStartYear,
    attendStartMonth, setAttendStartMonth,
    attendEndYear, setAttendEndYear,
    attendEndMonth, setAttendEndMonth,
    empFrom, setEmpFrom,
    empTo, setEmpTo,
    loading,
    result,
    status,
    errors,
    execute,
    preview,
  };
}
